import { Button, H3 } from "@sellar-io/components";
import { useState } from "react";
import TagManager from "react-gtm-module";
import { useMatch, useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import { SpinnerLegacy } from "~/components";
import * as ROUTES from "../../../../../constants/routes";
import { useSellarUser } from "~/hooks";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { useBusinessClaim } from "~/hooks/api/v2/business/useBusinessClaim";
import { useBusinessJoin } from "~/hooks/api/v2/business/useBusinessJoin";
import { useStorefrontLink } from "~/hooks/api/v2/storefrontLink/useStorefrontLink";
import { useSupplier } from "~/hooks/api/v2/supplier/useSupplier";
import { useUserNotificationPreferenceUpdate } from "~/hooks/api/v2/userNotificationPreference/useUserNotificationPreferenceUpdate";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../../hooks/useSignupContext.shared";
import { getJwtUrl } from "../../signupUrl";

export const UserOptInMarketingStepContent = ({
  userNotificationPreferenceId,
}: {
  userNotificationPreferenceId: number;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { changeBusiness } = useSellarUser();
  const {
    setStep,
    endOnboarding,
    signupDialogActive,
    hasAcceptedTermsAndConditions,
  } = useSignupContext();
  const [loading, setLoading] = useState(false);
  const updateUserNotificationPreferenceMutation =
    useUserNotificationPreferenceUpdate(userNotificationPreferenceId);
  const mutateJoinBusiness = useBusinessJoin();
  const claimBusinessMutation = useBusinessClaim();
  const matchedSupplierParams = useMatch(ROUTES.ORDER_BUYER.pattern);
  const supplierQueryEnabled =
    !!matchedSupplierParams?.params.supplierIdOrUrlId;
  const supplierQuery = useSupplier(
    matchedSupplierParams?.params.supplierIdOrUrlId,
    {
      enabled: supplierQueryEnabled,
      refetchOnWindowFocus: false,
    },
  );
  const businessUserInviteJwt = searchParams.get("jwt");
  const utmMedium = searchParams.get("utm_medium");
  const storefrontLinkPassword =
    searchParams.get("storefrontLink") ?? searchParams.get("priceTierLink");
  const storefrontLinkQueryEnabled =
    !!storefrontLinkPassword && !!supplierQuery.data;
  const storefrontLinkQuery = useStorefrontLink(
    storefrontLinkPassword,
    {
      SupplierId: supplierQuery.data?.id ?? 0,
    },
    {
      enabled: storefrontLinkQueryEnabled,
    },
  );

  const handleSuccessfulNotificationUpdate = () => {
    const continueUrlString = searchParams.get("continue");
    const jwtSearchParam = getJwtUrl(continueUrlString);
    if (jwtSearchParam) {
      navigate(`/jwt?jwt=${jwtSearchParam}`);
      return endOnboarding();
    }

    // if an invite link to join business, ignores buyer creation and legal entity creation - skips to confirmation page
    if (storefrontLinkQuery.data) {
      return claimOrderJoinStorefrontLinkBuyer();
    }
    return setStep(SignupFlowStep.BuyerCreationStep);
  };
  const onSubmit = () => {
    setLoading(true);
    return updateUserNotificationPreferenceMutation.mutate(
      {
        marketingOptIn: true,
      },
      {
        onSuccess: () => handleSuccessfulNotificationUpdate(),
      },
    );
  };

  const claimOrderJoinStorefrontLinkBuyer = async () => {
    const storefrontLink = storefrontLinkQuery.data;
    if (!storefrontLink) return setStep(SignupFlowStep.BuyerCreationStep);

    if (signupDialogActive) {
      const businessId =
        storefrontLink.SupplierRetailerConnection.Retailer.Business.id;
      const businessUsersLength =
        storefrontLink.SupplierRetailerConnection.Retailer.Business
          .BusinessUserCount;

      // storefront link - skips the buyer creation step and simply claims business

      if (!businessUsersLength) {
        return claimBusinessMutation.mutateAsync(
          {
            data: {
              id: businessId,
              hasAcceptedTermsAndConditions,
            },
            sellarMetadata: {
              storefrontLinkPassword,
              businessUserInviteJwt,
              utmMedium,
            },
          },
          {
            onSuccess: async (business) => {
              TagManager.dataLayer({
                dataLayer: {
                  event: "venue_claimed",
                  businessId: business.id,
                },
              });

              changeBusiness(business.id);

              return setStep(SignupFlowStep.ConfirmationStep);
            },
            onError: (error) => {
              const errorMessage =
                apiV2AssertError(error)?.response?.data.message;
              if (
                errorMessage ===
                "Can not claim an already claimed business with users."
              ) {
                return joinStorefrontLinkBusiness();
              }
              return setStep(SignupFlowStep.BuyerCreationStep);
            },
          },
        );
      }

      return joinStorefrontLinkBusiness();
    }
  };

  const joinStorefrontLinkBusiness = async () => {
    const storefrontLink = storefrontLinkQuery.data;
    if (!storefrontLink) return setStep(SignupFlowStep.BuyerCreationStep);
    const businessId =
      storefrontLink.SupplierRetailerConnection.Retailer.Business.id;
    return mutateJoinBusiness.mutateAsync(
      {
        data: { id: businessId, hasAcceptedTermsAndConditions },
        sellarMetadata: {
          storefrontLinkPassword,
          businessUserInviteJwt,
          utmMedium,
        },
      },
      {
        onSuccess: async (res) => {
          TagManager.dataLayer({
            dataLayer: {
              event: "venue_joined",
              retailerName:
                storefrontLink.SupplierRetailerConnection.Retailer.Business,
            },
          });

          if (res.business?.id) {
            changeBusiness(businessId);

            // TODO: could run credit check, will require moving the find company step to before marketing opt in

            return setStep(SignupFlowStep.ConfirmationStep);
          }
          return setStep(SignupFlowStep.BuyerCreationStep);
        },
        onError: () => {
          return setStep(SignupFlowStep.BuyerCreationStep);
        },
      },
    );
  };

  const isStorefrontLinkLoading =
    storefrontLinkQuery.isPending && storefrontLinkQueryEnabled;
  const isSupplierLoading = supplierQuery.isPending && supplierQueryEnabled;

  if (isStorefrontLinkLoading || isSupplierLoading) return <SpinnerLegacy />;

  return (
    <div className="flex flex-col items-center justify-center gap-4 text-center">
      <img
        className="mt-4 max-h-[300px] max-w-[320px] object-contain"
        src={
          "https://sellar.imgix.net/sellar-image-assets/app-assets/marketing-opt-in-asset-v5.png"
        }
      />
      <div className="flex flex-col gap-2">
        <H3>Sign up to get the good stuff straight to your inbox each week</H3>
      </div>
      <div className="mt-4 flex w-full flex-col items-center gap-2">
        <Button className="w-full" loading={loading} onClick={onSubmit}>
          Count me in
        </Button>
        <Button color="tertiary" onClick={handleSuccessfulNotificationUpdate}>
          No thanks
        </Button>
      </div>
    </div>
  );
};
